import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import { Users } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { WorkdayReportItem } from '../../../types/response'

interface EmployeeTableProps {
    data: WorkdayReportItem[]
}

const EmployeeTable: React.FC<EmployeeTableProps> = ({ data }) => {
    const { t } = useTranslation()

    // Define columns with custom renderers
    const columns = useMemo<DataTableColumn<WorkdayReportItem>[]>(
        () => [
            {
                key: 'employee_name',
                title: t('employee-name'),
                sortable: true,
            },
            {
                key: 'department_name',
                title: t('department'),
                sortable: true,
                render: (value) => (
                    <span className="text-gray-500 text-xs">{value}</span>
                ),
            },
            {
                key: 'total_work_days',
                title: t('workday.total_work_days_short'),
                sortable: true,
                align: 'center',
                render: (value) => (
                    <span className="font-medium text-primary">{value}</span>
                ),
            },
            {
                key: 'attendance_days',
                title: t('workday.attendance_short'),
                sortable: true,
                align: 'center',
                render: (value) => (
                    <span className="font-medium text-green-600">{value}</span>
                ),
            },
            {
                key: 'late_days',
                title: t('workday.late_days_short'),
                sortable: true,
                align: 'center',
                render: (value) => (
                    <span
                        className={`font-medium ${
                            (value as number) > 0
                                ? 'text-red-500'
                                : 'text-gray-300'
                        }`}
                    >
                        {value}
                    </span>
                ),
            },
            {
                key: 'late_minutes',
                title: t('workday.late_minutes_short'),
                sortable: true,
                align: 'center',
                render: (value) => (
                    <span
                        className={`font-medium ${
                            (value as number) > 0
                                ? 'text-orange-500'
                                : 'text-gray-300'
                        }`}
                    >
                        {value || 0}
                    </span>
                ),
            },
            {
                key: 'annual_leave_days',
                title: t('workday.leave_short'),
                sortable: true,
                align: 'center',
                render: (value) => (
                    <span className="font-medium text-blue-500">{value}</span>
                ),
            },
            {
                key: 'remote_days',
                title: t('workday.remote_short'),
                sortable: true,
                align: 'center',
                render: (value) => (
                    <span className="font-medium text-purple-500">{value}</span>
                ),
            },
        ],
        [t]
    )

    return (
        <div className="bg-white rounded-md p-4" data-tour="workday-table">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-primary" />
                    <Typogaphy className="font-medium text-primary">
                        {t('workday.employee_report')}
                    </Typogaphy>
                </div>
                <Typogaphy className="text-xs text-gray-500">
                    {data?.length || 0} {t('workday.employees')}
                </Typogaphy>
            </div>

            <DataTableCustomized
                columns={columns}
                rows={data || []}
                rowKey={(row) => String(row.employee_id)}
                showIndex
                size="small"
                emptyMessage={t('no-data')}
                pagination={{
                    page: 1,
                    pageSize: 10,
                    total: data?.length || 0,
                    pageSizeOptions: [10, 20, 50],
                }}
            />
        </div>
    )
}

export default EmployeeTable
